// Add or edit one dashboard banner slide, with a live preview. Uploaded files
// go to Shared Documents/Featured Content; the slide itself is a row in the
// SharePoint "Featured Content" list.
import { useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { Camera, CheckCircle2, Film, Image as ImageIcon, Link2, Loader2, MonitorPlay, Save, Type, Upload, X } from "lucide-react";
import { useGraphToken } from "../auth/useGraphToken";
import {
  isSharePointUrl,
  saveFeaturedRow,
  toSlide,
  uploadFeaturedFile,
  type FeaturedDraft,
  type FeaturedRow,
  type MediaType,
} from "../api/featured";
import { hyperlinkTooLong } from "../api/graph";
import type { FeaturedSlide } from "../data/featuredContent";
import { Featured } from "./dashboard/DashHero";

const MAX_VIDEO_MB = 250;
const MAX_IMAGE_MB = 15;
const VIDEO_TYPES = "video/mp4,video/webm,video/quicktime";
const IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/gif";

const EMPTY: FeaturedDraft = {
  title: "",
  description: "",
  tag: "",
  meta: "",
  mediaType: "Video",
  mediaUrl: "",
  posterUrl: "",
  buttonLabel: "",
  buttonUrl: "",
  sortOrder: 0,
  active: true,
  startDate: "",
  endDate: "",
};

const MEDIA_TYPES: { value: MediaType; label: string; icon: typeof Film }[] = [
  { value: "Video", label: "Video", icon: Film },
  { value: "Image", label: "Image", icon: ImageIcon },
  { value: "None", label: "Text only", icon: Type },
];

const fileName = (url: string) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || url);
  } catch {
    return url;
  }
};
const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
const isWebLink = (url: string) => /^https?:\/\/\S+$/i.test(url.trim());

function useObjectUrl(file: Blob | null): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!file) return setUrl(undefined);
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: ReactNode }) {
  return (
    <label className="add-item-field">
      <span className="add-item-label-row">
        <span className="add-item-label">
          {label}
          {required ? (
            <span aria-hidden className="add-item-required">
              *
            </span>
          ) : null}
        </span>
      </span>
      {children}
      {hint ? <span className="add-item-hint">{hint}</span> : null}
    </label>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; icon?: typeof Film }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="bn-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={`bn-seg-btn${value === o.value ? " is-active" : ""}`}
            onClick={() => onChange(o.value)}
          >
            {Icon ? <Icon size={14} aria-hidden /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Pick or drop one file. Shows the chosen file, or the one already saved. */
function DropZone({
  accept,
  file,
  current,
  hint,
  onFile,
}: {
  accept: string;
  file: File | null;
  current?: string;
  hint: string;
  onFile: (f: File | null) => void;
}) {
  const [over, setOver] = useState(false);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div className="bn-drop-wrap">
      <label
        className={`bn-drop${over ? " is-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <input type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
        <Upload size={18} aria-hidden />
        <span className="bn-drop-main">
          {file ? (
            <>
              <strong>{file.name}</strong> · {megabytes(file.size)}
            </>
          ) : current ? (
            <>
              Current: <strong>{fileName(current)}</strong>. Drop a file to replace it.
            </>
          ) : (
            <>
              <strong>Choose a file</strong> or drag it here
            </>
          )}
        </span>
        <span className="bn-drop-hint">{hint}</span>
      </label>
      {file ? (
        <button type="button" className="bn-icon-btn" onClick={() => onFile(null)} aria-label="Remove chosen file">
          <X size={15} />
        </button>
      ) : null}
    </div>
  );
}

type Source = "upload" | "link";

export function BannerSlideEditor({
  row,
  nextSortOrder,
  onClose,
  onSaved,
}: {
  row: FeaturedRow | null;
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const getToken = useGraphToken();
  const [draft, setDraft] = useState<FeaturedDraft>(() =>
    row ? { ...row } : { ...EMPTY, sortOrder: nextSortOrder }
  );
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [mediaSource, setMediaSource] = useState<Source>(
    row?.mediaUrl && !isSharePointUrl(row.mediaUrl) ? "link" : "upload"
  );
  const [posterSource, setPosterSource] = useState<Source>(
    row?.posterUrl && !isSharePointUrl(row.posterUrl) ? "link" : "upload"
  );
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const mediaLocal = useObjectUrl(mediaFile);
  const posterLocal = useObjectUrl(posterFile);
  const set = <K extends keyof FeaturedDraft>(key: K, value: FeaturedDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  // Preview of what's already saved or linked (SharePoint files need a lookup);
  // freshly chosen files are shown straight from the computer.
  const [remote, setRemote] = useState<FeaturedSlide | null>(null);
  const remoteMedia = mediaFile ? "" : draft.mediaUrl.trim();
  const remotePoster = posterFile ? "" : draft.posterUrl.trim();
  useEffect(() => {
    let alive = true;
    const t = window.setTimeout(async () => {
      try {
        const token = await getToken();
        const s = await toSlide(token, "preview", {
          ...EMPTY,
          mediaType: draft.mediaType,
          mediaUrl: remoteMedia,
          posterUrl: remotePoster,
        });
        if (alive) setRemote(s);
      } catch {
        if (alive) setRemote(null);
      }
    }, 400);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [draft.mediaType, remoteMedia, remotePoster, getToken]);

  const isVideo = draft.mediaType === "Video";
  const isImage = draft.mediaType === "Image";
  const preview: FeaturedSlide = {
    id: "preview",
    title: draft.title.trim() || "Slide title",
    description: draft.description.trim() || undefined,
    tag: draft.tag.trim() || undefined,
    meta: draft.meta.trim() || undefined,
    textOnly: draft.mediaType === "None",
    link: draft.buttonUrl.trim() ? { label: draft.buttonLabel.trim() || "Learn more", url: draft.buttonUrl.trim() } : undefined,
    image: (isVideo ? posterLocal : isImage ? mediaLocal : undefined) ?? remote?.image,
    videoUrl: isVideo ? mediaLocal ?? (remoteMedia || undefined) : undefined,
    player: isVideo ? (mediaLocal ? { kind: "video", src: mediaLocal } : remote?.player) : undefined,
  };

  const changeMediaType = (t: MediaType) => {
    if (t === draft.mediaType) return;
    setDraft((d) => ({ ...d, mediaType: t, mediaUrl: "", posterUrl: "" }));
    setMediaFile(null);
    setPosterFile(null);
  };

  const pickMedia = (f: File | null) => {
    setError(null);
    if (f) {
      const limit = isVideo ? MAX_VIDEO_MB : MAX_IMAGE_MB;
      const wanted = isVideo ? /^video\//.test(f.type) : /^image\//.test(f.type);
      if (!wanted) return setError(`That isn't ${isVideo ? "a video" : "an image"} file.`);
      if (f.size > limit * 1024 * 1024) return setError(`That file is ${megabytes(f.size)}; the limit is ${limit} MB.`);
    }
    setMediaFile(f);
  };

  const pickPoster = (f: File | null) => {
    setError(null);
    if (f && !/^image\//.test(f.type)) return setError("The poster must be an image.");
    if (f && f.size > MAX_IMAGE_MB * 1024 * 1024) return setError(`The poster is over ${MAX_IMAGE_MB} MB.`);
    setPosterFile(f);
  };

  const useCurrentFrame = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !mediaFile) return;
    const scale = Math.min(1, 1600 / v.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext("2d")?.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.86));
    if (!blob) return;
    const base = mediaFile.name.replace(/\.[^.]+$/, "");
    setPosterFile(new File([blob], `${base}-poster.jpg`, { type: "image/jpeg" }));
    setPosterSource("upload");
  };

  const problem = (() => {
    if (!draft.title.trim()) return "Add a title.";
    if ((isVideo || isImage) && !mediaFile && !draft.mediaUrl.trim())
      return isVideo ? "Add a video file or link." : "Add an image file or link.";
    const links: [string, string][] = [
      ["Media link", mediaFile ? "" : draft.mediaUrl],
      ["Poster link", posterFile || !isVideo ? "" : draft.posterUrl],
    ];
    for (const [name, url] of links) {
      if (url.trim() && !isWebLink(url)) return `${name} should start with https://.`;
      if (hyperlinkTooLong(url.trim())) return `${name} is too long for SharePoint (250 characters max).`;
    }
    const button = draft.buttonUrl.trim();
    if (button && !isWebLink(button) && !button.startsWith("/")) return "Button link should start with https:// or /.";
    if (hyperlinkTooLong(button)) return "Button link is too long for SharePoint (250 characters max).";
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) return "End date is before the start date.";
    return null;
  })();

  const onSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (problem || saving) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const next = { ...draft };
      // After each upload the link is kept and the file cleared, so a retry
      // after a failed save doesn't upload it twice.
      if (mediaFile && (isVideo || isImage)) {
        setStep(isVideo ? "Uploading video" : "Uploading image");
        next.mediaUrl = await uploadFeaturedFile(token, mediaFile, mediaFile.name, setProgress);
        setDraft((d) => ({ ...d, mediaUrl: next.mediaUrl }));
        setMediaFile(null);
        setMediaSource("upload");
      }
      if (posterFile && isVideo) {
        setStep("Uploading poster");
        setProgress(0);
        next.posterUrl = await uploadFeaturedFile(token, posterFile, posterFile.name, setProgress);
        setDraft((d) => ({ ...d, posterUrl: next.posterUrl }));
        setPosterFile(null);
        setPosterSource("upload");
      }
      setStep("Saving");
      setProgress(null);
      await saveFeaturedRow(token, next, row?.id);
      setSaved(true);
      window.setTimeout(onSaved, 600);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
      setStep("");
      setProgress(null);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="modal bn-editor" role="dialog" aria-modal="true" aria-labelledby="bn-editor-title">
        <div className="modal-accent" aria-hidden />
        <div className="modal-head">
          <div className="modal-head-icon" aria-hidden>
            <MonitorPlay size={22} />
          </div>
          <div className="modal-head-text">
            <span className="modal-kind">Dashboard banner</span>
            <h2 className="modal-title" id="bn-editor-title">
              {row ? `Edit “${row.title || "Untitled"}”` : "Add a slide"}
            </h2>
            <p className="add-item-subtitle">Shows in the featured spot on the dashboard banner.</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body bn-editor-body">
          <form className="add-item-form bn-editor-form" onSubmit={onSubmit}>
            <section className="add-item-section">
              <header className="add-item-section-head">
                <Type size={14} aria-hidden />
                <h3>The slide</h3>
              </header>
              <Field label="Title" required>
                <input type="text" value={draft.title} onChange={(e) => set("title", e.target.value)} maxLength={255} autoFocus />
              </Field>
              <div className="add-item-row">
                <Field label="Tag" hint="e.g. Demo, New release, Webinar">
                  <input type="text" value={draft.tag} onChange={(e) => set("tag", e.target.value)} maxLength={40} />
                </Field>
                <Field label="Detail" hint="e.g. 1 min, Nov 2026">
                  <input type="text" value={draft.meta} onChange={(e) => set("meta", e.target.value)} maxLength={40} />
                </Field>
              </div>
              <Field label="Description" hint="Shown on the banner for text-only slides.">
                <textarea rows={3} value={draft.description} onChange={(e) => set("description", e.target.value)} />
              </Field>
            </section>

            <section className="add-item-section">
              <header className="add-item-section-head">
                <Film size={14} aria-hidden />
                <h3>Media</h3>
              </header>
              <Segmented label="Media type" value={draft.mediaType} options={MEDIA_TYPES} onChange={changeMediaType} />

              {isVideo || isImage ? (
                <>
                  <Segmented
                    label={isVideo ? "Video source" : "Image source"}
                    value={mediaSource}
                    options={[
                      { value: "upload", label: "Upload a file", icon: Upload },
                      { value: "link", label: "Paste a link", icon: Link2 },
                    ]}
                    onChange={(v) => {
                      setMediaSource(v);
                      setMediaFile(null);
                    }}
                  />
                  {mediaSource === "upload" ? (
                    <DropZone
                      accept={isVideo ? VIDEO_TYPES : IMAGE_TYPES}
                      file={mediaFile}
                      current={draft.mediaUrl && isSharePointUrl(draft.mediaUrl) ? draft.mediaUrl : undefined}
                      hint={isVideo ? `MP4, WebM or MOV, up to ${MAX_VIDEO_MB} MB` : `JPG, PNG or WebP, up to ${MAX_IMAGE_MB} MB`}
                      onFile={pickMedia}
                    />
                  ) : (
                    <Field
                      label={isVideo ? "Video link" : "Image link"}
                      hint={isVideo ? "YouTube, Vimeo, a SharePoint/Stream video, or a direct .mp4 link." : "A direct link to the image."}
                    >
                      <input
                        type="url"
                        value={draft.mediaUrl}
                        onChange={(e) => set("mediaUrl", e.target.value)}
                        placeholder="https://…"
                      />
                    </Field>
                  )}
                  {isVideo && mediaLocal ? (
                    <div className="bn-scrub">
                      <video ref={videoRef} src={mediaLocal} controls muted playsInline preload="metadata" />
                      <button type="button" className="btn btn-sm" onClick={useCurrentFrame}>
                        <Camera size={14} aria-hidden /> Use this frame as the poster
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="add-item-section-sub">The banner shows the tag, title and description on a plain card.</p>
              )}

              {isVideo ? (
                <div className="bn-sub-block">
                  <span className="add-item-label">Poster (optional)</span>
                  <span className="add-item-hint">
                    The still shown before the video plays. Leave it empty to use the video's own thumbnail
                    {mediaLocal ? ", or pick a frame above." : "."}
                  </span>
                  <Segmented
                    label="Poster source"
                    value={posterSource}
                    options={[
                      { value: "upload", label: "Upload an image", icon: Upload },
                      { value: "link", label: "Paste a link", icon: Link2 },
                    ]}
                    onChange={(v) => {
                      setPosterSource(v);
                      setPosterFile(null);
                    }}
                  />
                  {posterSource === "upload" ? (
                    <DropZone
                      accept={IMAGE_TYPES}
                      file={posterFile}
                      current={draft.posterUrl && isSharePointUrl(draft.posterUrl) ? draft.posterUrl : undefined}
                      hint="16:9 works best, e.g. 1600 × 900"
                      onFile={pickPoster}
                    />
                  ) : (
                    <input
                      type="url"
                      aria-label="Poster link"
                      value={draft.posterUrl}
                      onChange={(e) => set("posterUrl", e.target.value)}
                      placeholder="https://…"
                    />
                  )}
                </div>
              ) : null}
            </section>

            <section className="add-item-section">
              <header className="add-item-section-head">
                <Link2 size={14} aria-hidden />
                <h3>Button (optional)</h3>
              </header>
              <div className="add-item-row">
                <Field label="Label" hint="e.g. Release notes">
                  <input type="text" value={draft.buttonLabel} onChange={(e) => set("buttonLabel", e.target.value)} maxLength={60} />
                </Field>
                <Field label="Link" hint="A web link, or a page here like /library">
                  <input type="text" value={draft.buttonUrl} onChange={(e) => set("buttonUrl", e.target.value)} placeholder="https://…" />
                </Field>
              </div>
            </section>

            <section className="add-item-section">
              <header className="add-item-section-head">
                <CheckCircle2 size={14} aria-hidden />
                <h3>When it shows</h3>
              </header>
              <label className="bn-switch">
                <input type="checkbox" checked={draft.active} onChange={(e) => set("active", e.target.checked)} />
                <span className="bn-switch-track" aria-hidden />
                <span>{draft.active ? "On: shows on the banner" : "Off: hidden from the banner"}</span>
              </label>
              <div className="add-item-row">
                <Field label="Start date" hint="Optional">
                  <input type="date" value={draft.startDate} onChange={(e) => set("startDate", e.target.value)} />
                </Field>
                <Field label="End date" hint="Optional; shows through this day">
                  <input type="date" value={draft.endDate} onChange={(e) => set("endDate", e.target.value)} />
                </Field>
              </div>
            </section>
          </form>

          <aside className="bn-preview" aria-label="Preview">
            <span className="add-item-label">Preview</span>
            <div className="db bn-preview-frame">
              <div className="db-hero bn-preview-hero">
                <Featured slides={[preview]} />
              </div>
            </div>
            {isVideo ? <span className="add-item-hint">Click the screen to try the video.</span> : null}
          </aside>
        </div>

        <div className="modal-foot bn-editor-foot">
          {saving && step ? (
            <span className="bn-progress" role="status">
              <Loader2 size={14} className="add-item-spin" aria-hidden />
              {step}
              {progress != null ? ` ${Math.round(progress * 100)}%` : "…"}
              {progress != null ? (
                <span className="bn-progress-bar" aria-hidden>
                  <span style={{ width: `${Math.round(progress * 100)}%` }} />
                </span>
              ) : null}
            </span>
          ) : error ? (
            <span className="bn-error" role="alert">
              {error}
            </span>
          ) : problem && (draft.title || row) ? (
            <span className="bn-note">{problem}</span>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={Boolean(problem) || saving || saved} onClick={() => void onSubmit()}>
            {saved ? (
              <>
                <CheckCircle2 size={14} /> Saved
              </>
            ) : (
              <>
                <Save size={14} /> {row ? "Save changes" : "Add slide"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
